import { useLayoutEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { requestHomeSection } from "@/lib/homeSections";
import { useSession } from "@/context/SessionContext";
import { productImageUrl } from "@/lib/api";
import FreeDeliveryTicker from "@/components/FreeDeliveryTicker";

const HERO_IMAGE_URL = "https://invictusa.eu/api/files/invictusa/products/7abc38ed-8b9c-422f-817c-f539eccd2314.webp";

const Line = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <span className="hero-line-enter block" style={{ "--hero-delay": `${delay}s` }}>
      {children}
    </span>
  </span>
);

const DanishFlag = () => (
  <svg viewBox="0 0 37 28" className="w-[18px] h-[13px] shrink-0 rounded-[1px] shadow-sm" aria-hidden="true" data-testid="hero-dk-flag">
    <rect width="37" height="28" fill="#C8102E" />
    <rect x="12" width="4" height="28" fill="#ffffff" />
    <rect y="12" width="37" height="4" fill="#ffffff" />
  </svg>
);

export default function Hero({ withAgentNotice = false }) {
  const { t, content } = useSession();
  const [usesInitialShell] = useState(() => Boolean(document.getElementById("initial-hero-shell")));

  useLayoutEffect(() => {
    // Ved første markedslæsning er shellen det ENESTE hero-billede og dermed den
    // tidlige LCP-kandidat. React lægger kun indhold over shellen; Shell rydder den
    // ved senere intern navigation væk fra forsiden.
    if (usesInitialShell) document.getElementById("initial-hero-shell")?.style.setProperty("z-index", "40");
  }, [usesInitialShell]);

  const heroSrc = HERO_IMAGE_URL;
  const hasHeroImage = true;
  const showHeroVisual = hasHeroImage && (!withAgentNotice || usesInitialShell);
  const isDrumAsset = (src) => String(src || "").includes("drum-25l");
  const badgeImage1 = !isDrumAsset(content?.badge_image_1) ? content?.badge_image_1 : "";
  const badgeImage2 = content?.badge_image_2 || "";
  // LANDEINDHOLD vælges fortsat af aktivt UI-sprog i Bootstrap. De uploadede
  // badge-filer er altid den synlige kilde; der findes ingen indbygget fallback.
  const primaryBadgeSrc = productImageUrl(badgeImage1);
  const secondaryBadgeSrc = productImageUrl(badgeImage2);

  return (
    <section
      id="forside"
      data-testid="hero"
      className={`relative z-50 grid-pattern overflow-hidden ${showHeroVisual ? "-mt-20 sm:-mt-24 lg:-mt-[220px] 2xl:-mt-[268px]" : "pt-20 sm:pt-24 lg:pt-28"}`}
    >
      {showHeroVisual && <div className={`relative ${usesInitialShell ? "" : "bg-black"}`}>
        {usesInitialShell ? (
          <div data-testid="hero-image-placeholder" className="block w-full aspect-[16/9]" aria-hidden="true" />
        ) : (
          <img src={heroSrc} alt="InvictusA" data-testid="hero-image" className="hero-img-fade block w-full h-auto" width="960" height="540" loading="eager" decoding="async" fetchPriority="high" />
        )}

        {/* Badge 1 (nederst venstre): den sprogvalgte LANDEINDHOLD-fil. */}
        {primaryBadgeSrc && (
          <img
            src={primaryBadgeSrc}
            alt=""
            data-testid="hero-badge"
            loading="eager"
            fetchPriority="high"
            className="hero-fade-down absolute bottom-[12%] left-[5%] lg:bottom-[14%] lg:left-[7%] w-32 sm:w-48 lg:w-60 z-20 drop-shadow-[0_12px_28px_rgba(0,161,228,0.3)]"
            style={{ "--hero-delay": "0.55s", "--hero-duration": "0.9s", "--hero-y": "-18px" }}
          />
        )}
      </div>}

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16 w-full">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 right-0 w-[46rem] h-[46rem] rounded-full bg-brand-blue/10 blur-[140px]" />
          <div className="absolute bottom-0 right-0 w-[30rem] h-[30rem] rounded-full bg-brand-blue/5 blur-[120px]" />
        </div>

        {/* Badge 2 (øverst højre): den sprogvalgte LANDEINDHOLD-fil. */}
        {secondaryBadgeSrc && (
          <img
            src={secondaryBadgeSrc}
            alt=""
            data-testid="hero-koeb-direkte-badge"
            className="hero-fade-down absolute -top-2 right-4 sm:-top-1 sm:right-6 w-28 sm:w-40 lg:w-48 z-10"
            style={{ "--hero-delay": "0.4s", "--hero-duration": "0.8s", "--hero-y": "-14px" }}
          />
        )}

        <div className="lg:max-w-[1000px]">
          {!showHeroVisual && primaryBadgeSrc && (
            <img
              src={primaryBadgeSrc} alt="" data-testid="hero-badge"
              className="hero-fade-down mb-7 w-32 sm:w-48 drop-shadow-[0_12px_28px_rgba(0,161,228,0.3)]" style={{ "--hero-delay": "0.25s", "--hero-duration": "0.9s", "--hero-y": "-18px" }}
            />
          )}
          <div
            data-testid="hero-made-in-dk"
            className="hero-fade-up mb-8" style={{ "--hero-delay": "0.15s", "--hero-duration": "0.7s", "--hero-y": "16px" }}
          >
            <div className="flex flex-col items-center gap-2 w-fit -rotate-[35deg]">
              <DanishFlag />
              <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-brand-blue leading-relaxed text-center whitespace-pre-line">
                {t("hero.madeIn")}
              </p>
            </div>
          </div>

          <FreeDeliveryTicker className="mt-2 mb-8 sm:mb-10" />

          <h1 className="font-display font-black uppercase leading-[0.92] tracking-tight text-[13vw] sm:text-7xl lg:text-[6.9rem]">
            <Line delay={0.25}>{t("hero.title1")}</Line>
            <Line delay={0.38} className="text-brand-blue">{t("hero.title2")}</Line>
            <Line delay={0.51}>
              <span className="text-outline">{t("hero.title3a")}</span> {t("hero.title3b")}
            </Line>
          </h1>

          <p
            className="hero-fade-up mt-8 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed" style={{ "--hero-delay": "0.85s", "--hero-duration": "0.8s", "--hero-y": "24px" }}
          >
            {t("hero.sub")}
          </p>

          <div
            className="hero-fade-up mt-10 flex flex-wrap items-center gap-4" style={{ "--hero-delay": "1s", "--hero-duration": "0.8s", "--hero-y": "24px" }}
          >
            <button
              data-testid="hero-products-btn"
              onClick={() => {
                requestHomeSection("emballager");
                scrollToId("emballager", { settle: true });
              }}
              className="group font-display text-base font-bold uppercase tracking-widest bg-brand-blue text-[#0B0E12] px-8 py-4 flex items-center gap-3 hover:bg-white transition-colors"
            >
              {t("hero.cta")}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
